// pages/Wizard/wizard.jsx - Simplified version (no times)

import React, { useState } from 'react';
import { Modal, Button, Card, Typography, Space, message, Row, Col, Statistic, Steps, Table, Tag, Switch, Alert, Divider } from 'antd';
import { 
  FrownOutlined, 
  SmileOutlined, 
  CheckCircleOutlined, 
  PlusOutlined, 
  MinusOutlined, 
  AppstoreOutlined as FridgeOutlined, 
  TrophyOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import './wizard.css';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const FirstLoginWizard = ({ visible, onComplete, onSubmitWizard }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [fridges, setFridges] = useState(2);
  const [freezers, setFreezers] = useState(1);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  
  // Opening hours state - simplified: just open/closed (no times)
  const [openingDays, setOpeningDays] = useState({
    0: true,  // Sunday - Open
    1: true,  // Monday - Open
    2: true,  // Tuesday - Open
    3: true,  // Wednesday - Open
    4: true,  // Thursday - Open
    5: true,  // Friday - Open
    6: true   // Saturday - Open
  });
  
  const [editingDay, setEditingDay] = useState(null);

  const steps = [
    { title: 'Welcome', icon: <TrophyOutlined /> },
    { title: 'Equipment Setup', icon: <FridgeOutlined /> },
    { title: 'Opening Days', icon: <ClockCircleOutlined /> },
    { title: 'Complete', icon: <CheckCircleOutlined /> },
  ];

  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const handleNext = () => {
    if (currentStep === 1 && fridges === 0 && freezers === 0) {
      message.warning('Please add at least one fridge or freezer');
      return;
    }
    setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleUpdateDay = (day, isOpen) => {
    setOpeningDays(prev => ({
      ...prev,
      [day]: isOpen
    }));
    setEditingDay(null);
  };

  const handleSubmit = async () => {
    if (fridges === 0 && freezers === 0) {
      message.warning('Please add at least one fridge or freezer');
      return;
    }

    setLoading(true);
    try {
      // Submit equipment and opening days (no times)
      await onSubmitWizard({ 
        fridges, 
        freezers,
        openingDays  // ✅ Just sends which days are open
      });
      setCompleted(true);
      message.success('Setup completed successfully!');
      setTimeout(() => {
        onComplete();
      }, 2000);
    } catch (error) {
      message.error('Failed to complete setup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: "Day", dataIndex: "dayName", key: "day", width: 150 },
    { 
      title: "Status", 
      dataIndex: "isOpen", 
      key: "status", 
      width: 100, 
      render: (isOpen) => <Tag color={isOpen ? "#6DB33F" : "red"}>{isOpen ? "Open" : "Closed"}</Tag> 
    },
    { 
      title: "Action", 
      key: "action", 
      width: 80, 
      render: (_, record) => <Button size="small" onClick={() => setEditingDay(record)}>Change</Button> 
    }
  ];

  const openingDaysArray = Object.keys(openingDays).map(day => ({
    day: parseInt(day),
    dayName: dayNames[parseInt(day)],
    isOpen: openingDays[parseInt(day)]
  }));

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="welcome-step">
            <div className="welcome-icon">
              <TrophyOutlined style={{ fontSize: 80, color: '#6DB33F' }} />
            </div>
            <Title level={2} style={{ textAlign: 'center', marginTop: 24 }}>
              Welcome to SFBB Pro!
            </Title>
            <Paragraph style={{ textAlign: 'center', fontSize: 16, marginTop: 16 }}>
              Let's get your kitchen set up for success. We'll help you configure
              your temperature monitoring equipment and operating days.
            </Paragraph>

                       <Paragraph style={{ textAlign: 'center', marginTop: 16 }}>
                Please make sure you customise your checklists before use.
                </Paragraph>
            <Card className="info-card" style={{ marginTop: 32 }}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div>
                  <CheckCircleOutlined style={{ color: '#6DB33F', marginRight: 8 }} />
                  <Text style={{color: 'black',fontWeight:'bold'}}>Set up your fridge and freezer monitoring</Text>
                </div>
                <div>
                  <CheckCircleOutlined style={{ color: '#6DB33F', marginRight: 8 }} />
                  <Text style={{color: 'black',fontWeight:'bold'}}>Configure which days your kitchen is open</Text>
                </div>
                <div>
                  <CheckCircleOutlined style={{ color: '#6DB33F', marginRight: 8 }} />
                  <Text style={{color: 'black',fontWeight:'bold'}}>Automatic checklist generation</Text>
                </div>
              </Space>
            </Card>
          </div>
        );

      case 1:
        return (
          <div className="setup-step">
            <Title level={3} style={{ textAlign: 'center', marginBottom: 32 }}>
              Configure Your Equipment
            </Title>
            
            <Row gutter={[32, 32]} justify="center">
              <Col xs={24} sm={12}>
                <Card className="equipment-card" hoverable>
                  <div className="equipment-header">
                    <FridgeOutlined style={{ fontSize: 48, color: '#6DB33F' }} />
                    <Title level={4} style={{ marginTop: 16 }}>Refrigerators</Title>
                  </div>
                  <div className="counter-container">
                    <Button
                      type="primary"
                      shape="circle"
                      size="large"
                      icon={<MinusOutlined />}
                      onClick={() => setFridges(Math.max(0, fridges - 1))}
                      disabled={fridges === 0}
                    />
                    <div className="counter-value">
                      <Statistic value={fridges} suffix="units" />
                    </div>
                    <Button
                      type="primary"
                      shape="circle"
                      size="large"
                      icon={<PlusOutlined />}
                      onClick={() => setFridges(Math.min(20, fridges + 1))}
                      disabled={fridges === 20}
                    />
                  </div>
                  <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 16 }}>
                    Fridges should operate at max 8°C (ideally below 5°C)
                  </Text>
                </Card>
              </Col>

              <Col xs={24} sm={12}>
                <Card className="equipment-card" hoverable>
                  <div className="equipment-header">
                    <div style={{ fontSize: 48 }}>❄️</div>
                    <Title level={4} style={{ marginTop: 16 }}>Freezers</Title>
                  </div>
                  <div className="counter-container">
                    <Button
                      type="primary"
                      shape="circle"
                      size="large"
                      icon={<MinusOutlined />}
                      onClick={() => setFreezers(Math.max(0, freezers - 1))}
                      disabled={freezers === 0}
                    />
                    <div className="counter-value">
                      <Statistic value={freezers} suffix="units" />
                    </div>
                    <Button
                      type="primary"
                      shape="circle"
                      size="large"
                      icon={<PlusOutlined />}
                      onClick={() => setFreezers(Math.min(20, freezers + 1))}
                      disabled={freezers === 20}
                    />
                  </div>
                  <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 16 }}>
                    Freezers should operate at -16°C to -22°C
                  </Text>
                </Card>
              </Col>
            </Row>

            {(fridges === 0 && freezers === 0) && (
              <div className="warning-message">
                <FrownOutlined style={{ fontSize: 24, color: '#ff4d4f', marginRight: 8 }} />
                <Text type="danger">Please add at least one fridge or freezer to continue</Text>
              </div>
            )}

            {(fridges > 0 || freezers > 0) && (
              <Card className="summary-card" style={{ marginTop: 24 }}>
                <Title level={5}>What will be created:</Title>
                <Space direction="vertical" size="small">
                  {fridges > 0 && (
                    <Text>{fridges} fridge{fridges > 1 ? 's' : ''} (opening &amp; closing temperature checks)</Text>
                  )}
                  {freezers > 0 && (
                    <Text>{freezers} freezer{freezers > 1 ? 's' : ''} (opening &amp; closing temperature checks)</Text>
                  )}
                  <Text>Temperature monitoring for food safety compliance</Text>
                </Space>
              </Card>
            )}
          </div>
        );

      case 2:
        return (
          <div className="setup-step">
            <Title level={3} style={{ textAlign: 'center', marginBottom: 16 }}>
              Set Your Opening Days
            </Title>
            <Paragraph style={{ textAlign: 'center', marginBottom: 24 }}>
              Select which days your kitchen is open. Checklists will only be generated for open days.
            </Paragraph>
            
            <Table 
              dataSource={openingDaysArray} 
              columns={columns} 
              pagination={false} 
              rowKey="day" 
              size="middle" 
            />
          </div>
        );

      case 3:
        const totalOpenDays = openingDaysArray.filter(d => d.isOpen).length;
        const totalTemperatureChecks = (fridges + freezers) * 2;
        
        return (
          <div className="complete-step">
            <div className="complete-icon">
              {completed ? (
                <CheckCircleOutlined style={{ fontSize: 80, color: '#6DB33F' }} />
              ) : (
                <SmileOutlined style={{ fontSize: 80, color: '#6DB33F' }} />
              )}
            </div>
            <Title level={3} style={{ textAlign: 'center', marginTop: 24 }}>
              {completed ? 'Setup Complete!' : 'Ready to Complete Setup?'}
            </Title>
            {!completed && (
              <>
                <Paragraph style={{ textAlign: 'center', marginTop: 16 }}>
                  Please customise your checklists after setup to match your kitchen.
                </Paragraph>
                <Paragraph style={{ textAlign: 'center', marginTop: 8, color: '#666' }}>
                  You can always edit these settings later in the Settings page.
                </Paragraph>
                
                <Card className="summary-card" style={{ marginTop: 16 }}>
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Title level={5}>Equipment Configuration:</Title>
                    {fridges > 0 && (
                      <Row justify="space-between">
                        <Col>Refrigerators:</Col>
                        <Col><strong>{fridges}</strong> units</Col>
                      </Row>
                    )}
                    {freezers > 0 && (
                      <Row justify="space-between">
                        <Col>Freezers:</Col>
                        <Col><strong>{freezers}</strong> units</Col>
                      </Row>
                    )}
                    
                    <Divider style={{ margin: '8px 0' }} />
                    
                    <Title level={5}>Opening Days Configuration:</Title>
                    <Row justify="space-between">
                      <Col>Operating Days:</Col>
                      <Col><strong>{totalOpenDays}</strong> days per week</Col>
                    </Row>
                    
                    <Divider style={{ margin: '8px 0' }} />
                    
                    <Title level={5}>Checklists Generated:</Title>
                    <Row justify="space-between">
                      <Col>Temperature checks per open day:</Col>
                      <Col><strong>{totalTemperatureChecks}</strong> (opening &amp; closing)</Col>
                    </Row>
                    <Row>
                      <Col>Opening checklists for open days</Col>
                    </Row>
                    <Row>
                      <Col>Closing checklists for open days</Col>
                    </Row>
                    <Row>
                      <Col>Weekly checklists</Col>
                    </Row>
                    
                    <Divider style={{ margin: '8px 0' }} />
                    
                    <Alert 
                      message="Don't forget to customise your checklists" 
                      description="Go to Settings → Checklists to add specific items like probe temperatures, cleaning tasks, and delivery checks."
                      type="info" 
                      showIcon 
                    />
                  </Space>
                </Card>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      title={null}
      open={visible}
      closable={false}
      footer={null}
      width={800}
      className="first-login-wizard-modal"
      maskClosable={false}
    >
      <div className="wizard-container">
        <Steps current={currentStep} items={steps} className="wizard-steps" />
        
        <div className="wizard-content">
          {renderStepContent()}
        </div>

        <div className="wizard-footer">
          {currentStep > 0 && currentStep < steps.length - 1 && (
            <Button onClick={handlePrev}>
              Back
            </Button>
          )}
          {currentStep < steps.length - 1 && (
            <Button type="primary" onClick={handleNext}>
              Next
            </Button>
          )}
          {currentStep === steps.length - 1 && !completed && (
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={loading}
              size="large"
              className="complete-button"
            >
              Complete Setup
            </Button>
          )}
          {completed && (
            <Button type="primary" onClick={onComplete} size="large">
              Go to Dashboard
            </Button>
          )}
        </div>
      </div>

      {/* Simple Edit Day Modal - No times, just open/closed */}
      <Modal 
        title={`Change ${editingDay?.dayName} Status`} 
        open={!!editingDay} 
        onCancel={() => setEditingDay(null)} 
        footer={null}
        className="wizard-day-modal"
      >
        {editingDay && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ marginBottom: 24 }}>
              <Switch 
                checked={editingDay.isOpen} 
                onChange={(checked) => setEditingDay({ ...editingDay, isOpen: checked })} 
                checkedChildren="Open" 
                unCheckedChildren="Closed"
                size="large"
              />
            </div>
            <div style={{ marginTop: 20, textAlign: "right" }}>
              <Button onClick={() => setEditingDay(null)}>Cancel</Button>
              <Button 
                type="primary" 
                style={{ marginLeft: 8 }} 
                onClick={() => handleUpdateDay(editingDay.day, editingDay.isOpen)}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </Modal>
  );
};

export default FirstLoginWizard;